/**
 * Formats a file size in bytes to a human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Gets the appropriate icon for a file based on its MIME type
 */
export const getFileIconByMimeType = (mimeType: string): string => {
  if (!mimeType) return 'file'
  
  if (mimeType.startsWith('image/')) {
    return 'image'
  } else if (mimeType.startsWith('video/')) {
    return 'video'
  } else if (mimeType.startsWith('audio/')) {
    return 'music'
  } else if (mimeType.includes('pdf')) {
    return 'file-text'
  } else if (mimeType.includes('word') || mimeType.includes('document')) {
    return 'file-text'
  } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return 'file-spreadsheet'
  } else if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
    return 'file-presentation'
  } else if (mimeType.includes('zip') || mimeType.includes('compressed')) {
    return 'archive'
  } else if (mimeType.includes('text/')) {
    return 'file-text'
  }
  
  return 'file'
}

/**
 * Gets the file extension from a filename
 */
export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

/**
 * Checks if a file is an image
 */
export const isImageFile = (mimeType: string): boolean => {
  return mimeType.startsWith('image/')
}

/**
 * Checks if a file is a video
 */
export const isVideoFile = (mimeType: string): boolean => {
  return mimeType.startsWith('video/')
}

/**
 * Checks if a file is a PDF
 */
export const isPdfFile = (mimeType: string): boolean => {
  return mimeType === 'application/pdf'
}

/**
 * Checks if a file can be previewed in the browser
 */
export const isPreviewable = (mimeType: string): boolean => {
  return isImageFile(mimeType) || isVideoFile(mimeType) || isPdfFile(mimeType)
}

/**
 * Converts a MIME type to a user-friendly file type name
 */
export const getFriendlyFileType = (mimeType: string): string => {
  if (!mimeType) return 'File'
  
  // Common document types
  if (mimeType === 'application/pdf') {
    return 'PDF Document'
  } else if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.endsWith('.xlsx') || mimeType.endsWith('.xls')) {
    return 'Excel Document'
  } else if (mimeType.includes('wordprocessing') || mimeType.includes('word') || mimeType.endsWith('.docx') || mimeType.endsWith('.doc')) {
    return 'Word Document'
  } else if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || mimeType.endsWith('.pptx') || mimeType.endsWith('.ppt')) {
    return 'Presentation'
  }
  
  // Media types
  if (mimeType.startsWith('image/')) {
    return 'Image'
  } else if (mimeType.startsWith('video/')) {
    return 'Video'
  } else if (mimeType.startsWith('audio/')) {
    return 'Audio'
  }
  
  // Text types
  if (mimeType === 'text/plain') {
    return 'Text File'
  } else if (mimeType === 'text/html') {
    return 'HTML File'
  } else if (mimeType === 'text/css') {
    return 'CSS File'
  } else if (mimeType === 'text/javascript' || mimeType === 'application/javascript') {
    return 'JavaScript File'
  }
  
  // Archive types
  if (mimeType.includes('zip') || mimeType.includes('compressed') || mimeType.includes('archive')) {
    return 'Archive'
  }
  
  // Fallback to a generic "File" if no specific type is identified
  return 'File'
}